import React, { memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import DOMPurify from 'dompurify';
import useAccountFormState from '../../hooks/useAccountFormState';
import Field from '../../../../../aem-core-components/components/Field';
import Select from '../../../../global/atoms/select/Select';
import Input from '../../../../global/atoms/input/Input';
import { ErrorInput as StorybookErrorInput } from '@sunbeltrentals/sbr-global-components';
import Checkbox from '../../../../global/atoms/Checkbox/Checkbox';
import Button from '../../../../global/atoms/button/button';
import SummarizeFormErrors from '../../../../global/modules/SummarizeErrors';
import AddressAutocomplete from '../../../../cca/components/addrAutocomplete/AddressAutocomplete';
import LicenseModal from '../licenseModal';
import VerifyAddressValidationModal from '../../../verifyAddressValidationModal';
import { LoadingIndicator } from '../../../../../aem-core-components';
import { formErrorFields, accountInfoFormErrorMapping } from '../../utils/formErrorMappingUtil';
import { generateStateList, getAEMCashFlowAlterableData, navigateToPath } from '../../utils/cashflowUtils';
import { useGuidedRegContext } from '../../../context/guidedRegContext';
import { SET_CURRENT_INDEX } from '../../../context/reducer';
import { useAnalyticsContext } from '../../../../../config/GoogleTagManagerEvents';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../../../constants/analyticsConstants/Ecommerce';
import ExternalLink from '../../../../../resources/images/external-link.svg';
import {
    COUNTRY,
    COUNTRY_LIST,
    ADDRESS,
    ADDRESS2,
    CITY,
    STATE,
    ZIP_CODE,
    IS_AGREE,
    IS_PRIVACY_POLICY,
    DRIVERS_LICENSE_STATE,
    DRIVERS_LICENSE_NUMBER,
    SELECT_COUNTRY,
    SELECT_PROVINCE,
    SELECT_STATE,
    STEP_NAMES,
    IS_SMS_OPTIN,
    IS_ELECTRONIC_MESSAGE
} from '../../constants';
import { AccountInfoTestId } from '../../../GRTestId';
import HelpModal from '../../../../global/modules/ccaHeader/HelpModal';
import RadioButton from '../../../../global/atoms/radioButton/RadioButton';

/**
 * Function to render the AccountInfo component.
 *
 * @return {JSX.Element} The AccountInfo component
 */
const AccountInfo = () => {
    const navigate = useNavigate();
    const { commonConfig = {}, accountScreenConfig = {} } = getAEMCashFlowAlterableData() || {};
    const [{ formData }, guidedRegDispatch] = useGuidedRegContext();
    const [isHelpModal, setIsHelpModal] = useState(false);
    const {
        accountState = {},
        handleDropDownChange = () => {},
        handleInputChange = () => {},
        handleZipCodeChange = () => {},
        handleCashLocationChange = () => {},
        setCashAddressManually = () => {},
        handleCheckboxChange = () => {},
        handleFocus = () => {},
        handleSubmit = () => {},
        onContinue = () => {},
        handleFieldInteraction = () => {},
        isCountryCanada,
        verifyAddressModal,
        setVerifyAddressModal,
        setAutoScrolling,
        saveFormErrorState,
        autoScrolling,
        isLoading,
        dlExist,
        translationValues
    } = useAccountFormState();
    const { requiredFieldsLabel = '', createAccountButtonLabel = '', errorMessageLabel = '' } = commonConfig || {};
    const {
        countryLabel = '',
        provinceLabel = '',
        addressLabel = '',
        termsAndConditions = [],
        zipcodeLabel = '',
        accountInfoTitle = '',
        yourAddressLabel = '',
        cityLabel = '',
        driversLicenseStateLabel = '',
        driversLicenseNumberLabel = '',
        stateLabel = '',
        postalLabel = '',
        addressLine2 = '',
        additionalInformationLabel = '',
        additionalInformationLabelCa = '',
        driversLicenseInfoTitle = '',
        smsOptIn = '',
        smsOptInCa = '',
        specialOffersLabel = '',
        termsAndConditionsLabel = '',
        antiSpamConsentDisclaimer = '',
        antiSpamConsentLabel = '',
        antiSpamConsentOption = '',
        antiSpamNotConsentDiscaimer = '',
        antiSpamNotConsentOption = '',
        smsDisabledTooltipText = '',
    } = accountScreenConfig || {};

    const errorFields =
        formErrorFields(
            accountState?.error,
            accountScreenConfig,
            accountInfoFormErrorMapping(accountState.data[COUNTRY]),
            translationValues.privacyPolicy
        ) || [];

    const { sendEventsForPageViewed, sendEventsForModalClosed } = useAnalyticsContext();

    useEffect(() => {
        if (formData[STEP_NAMES[0]]?.data?.firstName === '') {
            //navigate to step -1
            navigateToPath(navigate, '');
        }
    }, []);

    useEffect(() => {
        guidedRegDispatch({ type: SET_CURRENT_INDEX, payload: 1 });
        sendEventsForPageViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_PAGE_VIEWED,
            accountInfoTitle,
            window.location.href
        );
    }, []);

    useEffect(() => {
        if (dlExist) saveFormErrorState(DRIVERS_LICENSE_NUMBER, getErrorMsg());
    }, [dlExist]);

    const closeHelpModal = () => {
        setIsHelpModal(false);
    };

    const getErrorMsg = () => {
        return (
            <>
                {translationValues.driverLicenseExists}&nbsp;
                <a
                    onClick={() => setIsHelpModal(true)}
                    data-testid="SignInButton"
                    className="error_input__span__a"
                    aria-label={translationValues.contactUs}
                    role="alert"
                    tabIndex="0">
                    {translationValues.contactUs}
                </a>
            </>
        );
    };
    /**
     * Get the label for the state or province based on the country data.
     *
     * @return {string} The label for the state or province.
     */
    const getStateOrProvinceLabel = () => {
        return isCountryCanada ? provinceLabel : stateLabel;
    };

    /**
     * Function to get the label for zip or postal code based on the account state country data.
     *
     * @return {string} The label for zip or postal code.
     */
    const getZipOrPostalCodeLabel = () => {
        return isCountryCanada ? postalLabel : zipcodeLabel;
    };

    const handleHtmlData = htmlData => {
        if (htmlData?.includes?.('##')) {
            const splitHtml = htmlData.split('##');
            return (
                <div>
                    <span
                        className="stepform__review__html"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(splitHtml?.[0] ?? '', { ADD_ATTR: ['target'] })
                        }}
                    />
                    <ExternalLink />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(splitHtml?.[1] ?? '', { ADD_ATTR: ['target'] })
                        }}
                    />
                </div>
            );
        }

        return (
            <div>
                <span
                    className="stepform__review__html"
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(htmlData ?? '', { ADD_ATTR: ['target'] })
                    }}
                />
            </div>
        );
    };

    /**
     * This function returns a label based on the account state data.
     *
     * @return {string} the label for the account state data
     */
    const getAriaLabel = () => {
        return isCountryCanada ? SELECT_PROVINCE : SELECT_STATE;
    };

    const closeModal = () => {
        saveFormErrorState(ADDRESS, translationValues.validAddress);
        setVerifyAddressModal(false);
        setAutoScrolling(!autoScrolling);
        sendEventsForModalClosed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CTA_CLICKED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_CLOSE,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_BUTTON,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CCA_MODAL,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_CLOSE
        );
    };

    return (
        <div className={isLoading ? 'loading_overlay_background' : ''}>
            <section className="stepform__section">
                <div className="container">
                    <div className="row stepform__row">
                        <div className="col-lg-6">
                            <div className="form-heading stepform__heading">
                                <div className="stepform__steps" tabIndex="0">
                                    <strong>Step 2</strong> of 2
                                </div>
                                <h2 className="stepform__title">{accountInfoTitle}</h2>
                                {errorFields.length > 1 && (
                                    <SummarizeFormErrors
                                        errorFields={errorFields}
                                        authorableError={errorMessageLabel}
                                    />
                                )}
                                <small className="stepform__require-field-text">{requiredFieldsLabel}</small>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <fieldset className={`stepform__fieldset`}>
                                    <legend className={`stepform__legend`}>{yourAddressLabel}</legend>
                                    <div className="stepform__content">
                                        <Field
                                            label={countryLabel}
                                            inputAriaRequired={true}
                                            required={true}
                                            errorMsg={accountState?.error?.[COUNTRY]}>
                                            <Select
                                                selectedValue={accountState?.data?.[COUNTRY]}
                                                label={countryLabel}
                                                handleChange={value => handleDropDownChange(COUNTRY, value)}
                                                dropDownValues={COUNTRY_LIST}
                                                errorClass={accountState?.error?.[COUNTRY] && 'error_input__border'}
                                                elementId={COUNTRY}
                                                selectAriaLabel={SELECT_COUNTRY}
                                                onFocus={() => handleFocus(COUNTRY)}
                                                dataTestId={AccountInfoTestId.ACCOUNT_INFO_COUNTRY}
                                            />
                                            <StorybookErrorInput errorMsg={accountState?.error?.[COUNTRY]} />
                                        </Field>
                                        <div className="cmp-Field__field__root">
                                            <Field
                                                label={addressLabel}
                                                required={true}
                                                htmlFor={ADDRESS}
                                                errorMsg={accountState?.error?.[ADDRESS]}>
                                                <AddressAutocomplete
                                                    inputId={ADDRESS}
                                                    handleLocationChange={handleCashLocationChange}
                                                    inputAriaLabel={addressLabel}
                                                    prefilledAddress={accountState?.data?.[ADDRESS]}
                                                    isManualAddress={true}
                                                    setAddressManually={setCashAddressManually}
                                                    maxLength={100}
                                                    customCountryCode={accountState?.data?.[COUNTRY]}
                                                    onInputFocus={() => handleFocus(ADDRESS)}
                                                    isError={accountState?.error?.[ADDRESS] !== ''}
                                                />
                                                <StorybookErrorInput errorMsg={accountState?.error?.[ADDRESS]} />
                                            </Field>
                                        </div>
                                        <div className="cmp-Field__field__root">
                                            <Input
                                                name={ADDRESS2}
                                                data-testid={AccountInfoTestId.ACCOUNT_INFO_ADDRESS_INPUT}
                                                errorMsg={accountState?.error?.[ADDRESS2]}
                                                value={accountState?.data?.[ADDRESS2]}
                                                handleInputChange={handleInputChange}
                                                label={addressLine2}
                                                inputAriaRequired={true}
                                                required={false}
                                                showErrorIcon={true}
                                                onClick={() => handleFieldInteraction(ADDRESS2)}
                                            />
                                        </div>
                                        <div className="cmp-Field__field__root">
                                            <Input
                                                name={CITY}
                                                data-testid={AccountInfoTestId.ACCOUNT_INFO_CITY_INPUT}
                                                errorMsg={accountState?.error?.[CITY]}
                                                value={accountState?.data?.[CITY]}
                                                handleInputChange={handleInputChange}
                                                label={cityLabel}
                                                inputAriaRequired={true}
                                                required={true}
                                                showErrorIcon={true}
                                                onFocus={() => handleFocus(CITY)}
                                            />
                                        </div>
                                        <div className={`stepform__state_row`}>
                                            <Field
                                                label={getStateOrProvinceLabel()}
                                                inputAriaRequired={true}
                                                required={true}
                                                errorMsg={accountState?.error?.[STATE]}>
                                                <Select
                                                    selectedValue={accountState?.data?.[STATE]}
                                                    dataTestId={AccountInfoTestId.ACCOUNT_INFO_STATE_INPUT}
                                                    label={stateLabel}
                                                    handleChange={value => handleDropDownChange(STATE, value)}
                                                    dropDownValues={generateStateList(accountState?.data?.[COUNTRY])}
                                                    errorClass={accountState?.error?.[STATE] && 'error_input__border'}
                                                    elementId={STATE}
                                                    selectAriaLabel={getAriaLabel()}
                                                    onFocus={() => handleFocus(STATE)}
                                                />
                                                <StorybookErrorInput errorMsg={accountState?.error?.[STATE]} />
                                            </Field>

                                            <Input
                                                name={ZIP_CODE}
                                                dataTestId={AccountInfoTestId.ACCOUNT_INFO_POSTAL_CODE_INPUT}
                                                errorMsg={accountState?.error?.[ZIP_CODE]}
                                                value={accountState?.data?.[ZIP_CODE]}
                                                handleInputChange={handleZipCodeChange}
                                                label={getZipOrPostalCodeLabel()}
                                                inputAriaRequired={true}
                                                required={true}
                                                showErrorIcon={true}
                                                onFocus={() => handleFocus(ZIP_CODE)}
                                            />
                                        </div>
                                        <hr className="stepform__divider" />
                                        <div>
                                            <div className="margin-bottom16">
                                                <strong>
                                                    {isCountryCanada
                                                        ? additionalInformationLabelCa
                                                        : additionalInformationLabel}
                                                </strong>
                                            </div>
                                            {!isCountryCanada && (
                                                <>
                                                    <Field
                                                        label={driversLicenseStateLabel}
                                                        inputAriaRequired={true}
                                                        required={true}
                                                        errorMsg={accountState?.error?.[DRIVERS_LICENSE_STATE]}>
                                                        <Select
                                                            selectedValue={accountState?.data?.[DRIVERS_LICENSE_STATE]}
                                                            label={driversLicenseStateLabel}
                                                            handleChange={value =>
                                                                handleDropDownChange(DRIVERS_LICENSE_STATE, value)
                                                            }
                                                            dropDownValues={generateStateList(
                                                                accountState?.data?.[COUNTRY]
                                                            )}
                                                            errorClass={
                                                                accountState?.error?.[DRIVERS_LICENSE_STATE] &&
                                                                'error_input__border'
                                                            }
                                                            elementId={DRIVERS_LICENSE_STATE}
                                                            selectAriaLabel={SELECT_STATE}
                                                            onFocus={() => handleFocus(DRIVERS_LICENSE_STATE)}
                                                            dataTestId={AccountInfoTestId.ACCOUNT_INFO_LICENCE_STATE}
                                                        />
                                                        <StorybookErrorInput
                                                            errorMsg={accountState?.error?.[DRIVERS_LICENSE_STATE]}
                                                        />
                                                    </Field>
                                                    <div className="cmp-Field__field__root">
                                                        <Input
                                                            name={DRIVERS_LICENSE_NUMBER}
                                                            errorMsg={accountState?.error?.[DRIVERS_LICENSE_NUMBER]}
                                                            value={accountState?.data?.[DRIVERS_LICENSE_NUMBER]}
                                                            handleInputChange={handleInputChange}
                                                            label={driversLicenseNumberLabel}
                                                            dataTestId={AccountInfoTestId.ACCOUNT_INFO_LICENCE_NUMBER}
                                                            inputAriaRequired={true}
                                                            required={true}
                                                            showErrorIcon={true}
                                                            maxLength={15}
                                                            onFocus={() => handleFocus(DRIVERS_LICENSE_NUMBER)}
                                                        />

                                                        <div
                                                            className={`driver-license__driver-info ${
                                                                accountState?.error?.[DRIVERS_LICENSE_NUMBER]
                                                                    ? 'dl_error'
                                                                    : ''
                                                            }`}>
                                                            <small
                                                                className="driver-license__driver-info-text"
                                                                tabIndex="0">
                                                                {driversLicenseInfoTitle}
                                                            </small>

                                                            <LicenseModal />
                                                        </div>
                                                    </div>
                                                    <div className="margin-bottom16">
                                                        <strong>{specialOffersLabel}</strong>
                                                    </div>
                                                </>
                                            )}
                                            <div className="margin-bottom16">
                                                <Checkbox
                                                    isChecked={accountState?.data?.[IS_AGREE]}
                                                    onClick={handleCheckboxChange}
                                                    id={IS_AGREE}
                                                    label={termsAndConditions[0]}
                                                    dataTestId={AccountInfoTestId.ACCOUNT_INFO_ACCOUNT_CHECKBOX}
                                                    disabled={accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'No'}
                                                    tooltipContent={
                                                        isCountryCanada && accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'No'
                                                            ? smsDisabledTooltipText
                                                            : undefined
                                                    }
                                                    tooltipId="specialOffersTooltip"
                                                />
                                                {isCountryCanada && accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'No' && (
                                                    <ReactTooltip
                                                        id="specialOffersTooltip"
                                                        className="custom-tooltip"
                                                    />
                                                )}
                                            </div>

                                            <div
                                                className={`smsOptIn ${
                                                    isCountryCanada ? 'margin-bottom16' : 'margin-bottom24'
                                                }`}>
                                                <Checkbox
                                                    isChecked={accountState?.data?.[IS_SMS_OPTIN]}
                                                    onClick={handleCheckboxChange}
                                                    id={IS_SMS_OPTIN}
                                                    customAriaLabel="Click here for getting the promotional messages"
                                                    label={handleHtmlData(isCountryCanada ? smsOptInCa : smsOptIn)}
                                                    dataTestId={AccountInfoTestId.ACCOUNT_INFO_SMS_OPTIN_CHECKBOX}
                                                    disabled={accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'No'}
                                                    tooltipContent={
                                                        isCountryCanada && accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'No'
                                                            ? smsDisabledTooltipText
                                                            : undefined
                                                    }
                                                    tooltipId="smsOptInTooltip"
                                                />
                                                {isCountryCanada && accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'No' && (
                                                    <ReactTooltip
                                                        id="smsOptInTooltip"
                                                        className="custom-tooltip"
                                                    />
                                                )}
                                            </div>
                                            {!isCountryCanada && (
                                                <>
                                                    <hr className="stepform__divider" />
                                                    <div className="margin-bottom16">
                                                        <strong>{termsAndConditionsLabel}</strong>
                                                    </div>
                                                    <div>
                                                        <Checkbox
                                                            customClassName={`privacy-policy ${
                                                                accountState?.error?.[IS_PRIVACY_POLICY] ? 'checkbox_error' : ''
                                                            }`}
                                                            isChecked={accountState?.data?.[IS_PRIVACY_POLICY]}
                                                            onClick={handleCheckboxChange}
                                                            id={IS_PRIVACY_POLICY}
                                                            dataTestId={AccountInfoTestId.ACCOUNT_INFO_PRIVACY_POLICY}
                                                            customAriaLabel="Click here for accepting the terms and conditions and privacy policy"
                                                            label={handleHtmlData(
                                                                isCountryCanada ? termsAndConditions[2] : termsAndConditions[1]
                                                            )}
                                                        />
                                                        <StorybookErrorInput errorMsg={accountState?.error?.[IS_PRIVACY_POLICY]} />
                                                    </div>
                                                </>
                                            )}
                                            {isCountryCanada && (
                                                <div>
                                                    <div className="margin-bottom16">
                                                        {' '}
                                                        <strong>{antiSpamConsentLabel}</strong>{' '}
                                                    </div>
                                                    <div
                                                        role="radiogroup"
                                                        className={`stepform__radiogroup ${
                                                            accountState.error?.[IS_ELECTRONIC_MESSAGE] ? 'radio_error' : ''
                                                        }`}
                                                        id={IS_ELECTRONIC_MESSAGE}>
                                                        <div>
                                                            <RadioButton
                                                                dataTestId="review_info_radio_button"
                                                                customClassName="stepform__review__radio"
                                                                id="isElectronicMessageYes"
                                                                name={IS_ELECTRONIC_MESSAGE}
                                                                value="Yes"
                                                                checked={accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'Yes'}
                                                                handleRadioChange={handleInputChange}
                                                                label={antiSpamConsentOption}
                                                            />
                                                            <small className="stepform__review__radio__subheading radio_spacing">
                                                                {handleHtmlData(antiSpamConsentDisclaimer)}
                                                            </small>
                                                        </div>
                                                        <div className="margin-bottom8">
                                                            <RadioButton
                                                                customClassName="stepform__review__radio margin-top24"
                                                                id="isElectronicMessageNo"
                                                                name={IS_ELECTRONIC_MESSAGE}
                                                                value="No"
                                                                checked={accountState.data?.[IS_ELECTRONIC_MESSAGE] === 'No'}
                                                                handleRadioChange={handleInputChange}
                                                                label={antiSpamNotConsentOption}
                                                            />
                                                            <small className="stepform__review__radio__subheading radio_spacing">
                                                                {antiSpamNotConsentDiscaimer}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <StorybookErrorInput errorMsg={accountState.error?.[IS_ELECTRONIC_MESSAGE]} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </fieldset>
                                <Button
                                    className={`button button-primary button-block`}
                                    type="button"
                                    dataTestid={AccountInfoTestId.CONTINUE_BUTTON}
                                    onClick={onContinue}
                                    buttonAriaLabel={createAccountButtonLabel}>
                                    {createAccountButtonLabel}
                                </Button>
                            </form>
                        </div>
                    </div>
                </div>
                {verifyAddressModal && (
                    <VerifyAddressValidationModal verifyAddressModal={verifyAddressModal} onClose={closeModal} />
                )}
            </section>
            {isLoading && (
                <div className="globalmodal__overlay loading_overlay">
                    <LoadingIndicator />
                </div>
            )}

            {isHelpModal && <HelpModal helpModal={isHelpModal} closeHelpModal={closeHelpModal} />}
        </div>
    );
};

export default memo(AccountInfo);
